'use client';

import React, { useState, useCallback } from 'react';
import { Sparkles, PenTool, Send } from 'lucide-react';
import AIGenerationPanel from './AIGenerationPanel';
import CanvasEditor from './CanvasEditor';
import PublishModal from './PublishModal';

export type FlyerMode = 'ai' | 'canvas';

export interface CanvasElement {
  id: string;
  type: 'text' | 'shape' | 'image';
  x: number;
  y: number;
  width: number;
  height: number;
  content?: string;
  fontSize?: number;
  fontWeight?: string;
  color?: string;
  bgColor?: string;
  shapeType?: 'rect' | 'circle' | 'triangle';
  imageUrl?: string;
  imageAlt?: string;
  zIndex: number;
  textAlign?: 'left' | 'center' | 'right';
}

export interface FlyerState {
  width: number;
  height: number;
  bgColor: string;
  elements: CanvasElement[];
  templateName: string;
}

const TEMPLATES: { id: string; name: string; platform: string; width: number; height: number; bgColor: string; elements: CanvasElement[] }[] = [
  {
    id: 'fb-post',
    name: 'Facebook Post',
    platform: 'Facebook',
    width: 1200,
    height: 630,
    bgColor: '#1877F2',
    elements: [
      { id: 'el-1', type: 'text', x: 60, y: 180, width: 700, height: 80, content: 'Your Headline Here', fontSize: 48, fontWeight: '700', color: '#FFFFFF', zIndex: 1, textAlign: 'left' },
      { id: 'el-2', type: 'text', x: 60, y: 280, width: 600, height: 40, content: 'Add your compelling subtitle or call to action', fontSize: 22, fontWeight: '400', color: '#E8F0FE', zIndex: 1, textAlign: 'left' },
      { id: 'el-3', type: 'shape', x: 60, y: 360, width: 180, height: 52, shapeType: 'rect', bgColor: '#FFFFFF', color: '#1877F2', content: 'Learn More', fontSize: 18, fontWeight: '600', zIndex: 2, textAlign: 'center' },
    ],
  },
  {
    id: 'ig-square',
    name: 'Instagram Square',
    platform: 'Instagram',
    width: 1080,
    height: 1080,
    bgColor: '#E1306C',
    elements: [
      { id: 'el-1', type: 'text', x: 80, y: 380, width: 920, height: 100, content: 'Your Brand Story', fontSize: 56, fontWeight: '800', color: '#FFFFFF', zIndex: 1, textAlign: 'center' },
      { id: 'el-2', type: 'text', x: 80, y: 500, width: 920, height: 50, content: 'Engage your audience with powerful visuals', fontSize: 24, fontWeight: '400', color: '#FFE0EC', zIndex: 1, textAlign: 'center' },
      { id: 'el-3', type: 'shape', x: 390, y: 600, width: 300, height: 56, shapeType: 'rect', bgColor: '#FFFFFF', color: '#E1306C', content: 'Shop Now', fontSize: 20, fontWeight: '700', zIndex: 2, textAlign: 'center' },
    ],
  },
  {
    id: 'ig-story',
    name: 'Instagram Story',
    platform: 'Instagram',
    width: 1080,
    height: 1920,
    bgColor: '#6C47FF',
    elements: [
      { id: 'el-1', type: 'text', x: 80, y: 700, width: 920, height: 120, content: 'Big Sale Today!', fontSize: 72, fontWeight: '900', color: '#FFFFFF', zIndex: 1, textAlign: 'center' },
      { id: 'el-2', type: 'text', x: 80, y: 850, width: 920, height: 60, content: 'Up to 50% off on all products', fontSize: 30, fontWeight: '400', color: '#E0D9FF', zIndex: 1, textAlign: 'center' },
      { id: 'el-3', type: 'shape', x: 340, y: 980, width: 400, height: 70, shapeType: 'rect', bgColor: '#FF6B35', color: '#FFFFFF', content: 'Swipe Up', fontSize: 24, fontWeight: '700', zIndex: 2, textAlign: 'center' },
    ],
  },
  {
    id: 'blank',
    name: 'Blank Canvas',
    platform: 'Custom',
    width: 1200,
    height: 800,
    bgColor: '#FFFFFF',
    elements: [],
  },
];

export default function FlyerBuilderContent() {
  const [mode, setMode] = useState<FlyerMode>('ai');
  const [flyerState, setFlyerState] = useState<FlyerState>({
    width: 1200,
    height: 630,
    bgColor: '#1877F2',
    elements: TEMPLATES[0].elements,
    templateName: TEMPLATES[0].name,
  });
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishImageBase64, setPublishImageBase64] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);

  const handleTemplateSelect = (templateId: string) => {
    const tpl = TEMPLATES.find((t) => t.id === templateId);
    if (!tpl) return;
    setFlyerState({
      width: tpl.width,
      height: tpl.height,
      bgColor: tpl.bgColor,
      elements: tpl.elements.map((el) => ({ ...el })),
      templateName: tpl.name,
    });
    setMode('canvas');
  };

  const handleAIGenerated = (imageBase64: string) => {
    setFlyerState((prev) => ({
      ...prev,
      bgColor: '#FFFFFF',
      elements: [
        {
          id: `el-ai-${Date.now()}`,
          type: 'image',
          x: 0,
          y: 0,
          width: prev.width,
          height: prev.height,
          imageUrl: `data:image/png;base64,${imageBase64}`,
          imageAlt: 'AI generated flyer',
          zIndex: 0,
        },
      ],
      templateName: 'AI Generated',
    }));
    setMode('canvas');
  };

  const handleOpenPublish = useCallback(async () => {
    if (mode === 'canvas') {
      setIsCapturing(true);
      try {
        const capture = (window as unknown as Record<string, unknown>).__flyerCapture as (() => Promise<string | null>) | undefined;
        if (capture) {
          const base64 = await capture();
          setPublishImageBase64(base64);
        } else {
          setPublishImageBase64(null);
        }
      } catch {
        setPublishImageBase64(null);
      } finally {
        setIsCapturing(false);
      }
    } else {
      // In AI mode, check if there's an AI-generated image element
      const aiEl = flyerState.elements.find((el) => el.type === 'image' && el.imageUrl?.startsWith('data:'));
      setPublishImageBase64(aiEl?.imageUrl ?? null);
    }
    setPublishOpen(true);
  }, [mode, flyerState.elements]);

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Flyer Builder</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Create stunning ad flyers with AI or the visual canvas editor</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Publish Button */}
          <button
            onClick={handleOpenPublish}
            disabled={isCapturing}
            className="btn-primary flex items-center gap-2 px-4 py-2"
          >
            {isCapturing ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Capturing...
              </>
            ) : (
              <>
                <Send size={15} />
                Publish
              </>
            )}
          </button>
          {/* Mode Toggle */}
          <div className="flex items-center gap-1 p-1 bg-muted rounded-xl">
            <button
              onClick={() => setMode('ai')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
                mode === 'ai' ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Sparkles size={15} />
              AI Generate
            </button>
            <button
              onClick={() => setMode('canvas')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
                mode === 'canvas' ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <PenTool size={15} />
              Canvas Editor
            </button>
          </div>
        </div>
      </div>

      {mode === 'ai' ? (
        <AIGenerationPanel
          templates={TEMPLATES}
          onTemplateSelect={handleTemplateSelect}
          onAIGenerated={handleAIGenerated}
          flyerState={flyerState}
          setFlyerState={setFlyerState}
        />
      ) : (
        <CanvasEditor
          flyerState={flyerState}
          setFlyerState={setFlyerState}
          templates={TEMPLATES}
          onTemplateSelect={handleTemplateSelect}
          onCaptureFlyer={() => {}}
        />
      )}

      <PublishModal
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        flyerImageBase64={publishImageBase64}
      />
    </div>
  );
}
